import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ChevronLeft, ChevronRight, ImagePlus, Loader2 } from "lucide-react";

export const StoreGalleryTab = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/settings").then((r) => setPhotos(r.data.store_gallery || []));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post("/admin/store-gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(r.data.store_gallery || []);
      toast.success("Photo added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const saveCaption = async (id, caption) => {
    const r = await api.put(`/admin/store-gallery/${id}`, { caption });
    setPhotos(r.data.store_gallery || []);
  };
  const remove = async (id) => {
    const r = await api.delete(`/admin/store-gallery/${id}`);
    setPhotos(r.data.store_gallery || []);
    toast.success("Removed");
  };
  const move = async (idx, dir) => {
    const next = [...photos];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setPhotos(next);
    const r = await api.post("/admin/store-gallery/reorder", { order: next.map((p) => p.id) });
    setPhotos(r.data.store_gallery || []);
  };

  return (
    <div data-testid="store-gallery-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div>
          <div className="font-heading text-2xl text-brand-navy">Gallery</div>
          <p className="text-slate-500 text-sm">Store & warehouse photos shown on the public Gallery page.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="store-gallery-upload-btn"
          className="inline-flex items-center gap-2 bg-brand-red text-white rounded-full h-10 px-5 font-bold active:scale-95 disabled:opacity-60">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photo
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="store-gallery-file-input" />
      </div>

      {photos.length === 0 ? (
        <div className="mt-8 text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
          <ImagePlus className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-bold text-slate-500 mt-3">No gallery photos yet</div>
          <p className="text-slate-400 text-sm">Upload your first store photo to show it on the Gallery page.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <div key={p.id} data-testid={`store-gallery-item-${p.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="relative aspect-square bg-slate-50">
                <img src={mediaUrl(p.url)} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`store-gallery-left-${p.id}`} className="bg-white/90 rounded-full p-1 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === photos.length - 1} data-testid={`store-gallery-right-${p.id}`} className="bg-white/90 rounded-full p-1 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                </div>
                <button onClick={() => remove(p.id)} data-testid={`store-gallery-delete-${p.id}`} className="absolute top-2 right-2 bg-brand-red text-white rounded-full p-1.5"><Trash2 className="h-4 w-4" /></button>
              </div>
              <input
                defaultValue={p.caption || ""}
                onBlur={(e) => saveCaption(p.id, e.target.value)}
                placeholder="Add a caption…"
                data-testid={`store-gallery-caption-${p.id}`}
                className="w-full text-sm px-3 h-10 border-t border-slate-100 focus:outline-none"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
